import React, { useState, useEffect, useCallback } from 'react';
import {
  listProjects, createProject, deleteProject,
  listTimeEntries, createTimeEntry, deleteTimeEntry, invoiceTimeEntries,
  profitabilityDashboard, profitabilityClients, profitabilityServices,
  getClients,
} from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import PageHelpButton from '../components/help/PageHelpButton';
import {
  Briefcase, Plus, Loader2, Clock, TrendingUp, Trash2, Receipt,
  Folder, AlertTriangle, CheckCircle2, DollarSign, Sparkles, Hash,
} from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: 'EUR' }).format(n || 0);
const fmtHours = (m) => {
  if (!m) return '0h';
  if (m < 60) return `${m}min`;
  const h = m / 60;
  return h % 1 === 0 ? `${h}h` : `${h.toFixed(2)}h`;
};

const STATUS_CLS = {
  active: 'bg-emerald-100 text-emerald-700',
  paused: 'bg-amber-100 text-amber-700',
  completed: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-stone-100 text-stone-500',
};

const ProjectsPage = () => {
  const [tab, setTab] = useState('projects');
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [entries, setEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [prof, setProf] = useState(null);
  const [profClients, setProfClients] = useState([]);
  const [profServices, setProfServices] = useState([]);
  const [showProject, setShowProject] = useState(false);
  const [showEntry, setShowEntry] = useState(false);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [grouping, setGrouping] = useState('project');

  const load = useCallback(async () => {
    try {
      const [p, te, c, pd, pc, ps] = await Promise.all([
        listProjects(),
        listTimeEntries(),
        getClients(),
        profitabilityDashboard(),
        profitabilityClients(),
        profitabilityServices(),
      ]);
      setProjects(p.data?.items || []);
      setEntries(te.data?.items || []);
      setClients(c.data || []);
      setProf(pd.data);
      setProfClients(pc.data?.items || []);
      setProfServices(ps.data?.items || []);
    } catch (e) {
      toast.error('Error cargando');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleSelect = (id) => {
    setSelectedEntries((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  };

  const handleInvoice = async () => {
    if (!selectedEntries.length) return toast.error('Selecciona al menos un registro');
    try {
      const r = await invoiceTimeEntries({ entry_ids: selectedEntries, grouping });
      toast.success(`Factura ${r.data.invoice_number} · ${fmt(r.data.total)}`);
      setSelectedEntries([]);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#FF6B4A]" /></div>;

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-3xl sm:text-4xl font-heading font-extrabold text-stone-900 flex items-center gap-3">
              <Briefcase className="w-8 h-8 text-[#FF6B4A]" />
              Proyectos & Horas
            </h1>
            <p className="text-sm text-stone-500 mt-1">Time tracking + rentabilidad por proyecto/cliente/servicio</p>
          </div>
          <PageHelpButton moduleKey="projects" variant="icon" />
        </div>
        <div className="flex gap-2">
          {tab === 'projects' && <Button onClick={() => setShowProject(true)} className="bg-[#FF6B4A] text-white" data-testid="new-project-btn"><Plus className="w-4 h-4 mr-1" /> Nuevo proyecto</Button>}
          {tab === 'time' && <Button onClick={() => setShowEntry(true)} className="bg-[#FF6B4A] text-white" data-testid="new-entry-btn"><Plus className="w-4 h-4 mr-1" /> Registrar horas</Button>}
        </div>
      </div>

      <div className="flex gap-1 bg-white rounded-xl border border-stone-100 p-1 w-fit">
        {[
          { k: 'projects', l: 'Proyectos', i: Briefcase },
          { k: 'time', l: 'Horas', i: Clock },
          { k: 'profit', l: 'Rentabilidad', i: TrendingUp },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition flex items-center gap-1.5 ${tab === t.k ? 'bg-stone-900 text-white' : 'text-stone-600 hover:bg-stone-100'}`}
            data-testid={`pj-tab-${t.k}`}>
            <t.i className="w-4 h-4" /> {t.l}
          </button>
        ))}
      </div>

      {tab === 'projects' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {projects.length === 0 ? (
            <div className="md:col-span-3 bg-white rounded-2xl border border-stone-100 p-10 text-center">
              <Folder className="w-12 h-12 mx-auto text-stone-300 mb-2" />
              <p className="text-stone-500">Sin proyectos. Crea el primero para empezar a trackear horas y rentabilidad.</p>
            </div>
          ) : projects.map((p) => (
            <div key={p.id} className="bg-white rounded-2xl border border-stone-100 p-4 hover:shadow-md transition" data-testid={`project-card-${p.id}`}>
              <div className="flex items-start gap-2 mb-2">
                <div className="w-2.5 h-2.5 rounded-full mt-1.5" style={{ background: p.color || '#FF6B4A' }} />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-stone-900 truncate">{p.name}</p>
                  <p className="text-xs text-stone-500 truncate">{p.client_name}</p>
                </div>
                <Badge className={`${STATUS_CLS[p.status] || ''} border-0 text-[10px]`}>{p.status}</Badge>
              </div>
              <div className="flex justify-between text-xs text-stone-500 mt-3 pt-3 border-t border-stone-100">
                <span>{p.billing_type === 'hourly' ? `${fmt(p.default_hourly_rate || 0)}/h` : p.billing_type}</span>
                {p.budget_amount && <span>{fmt(p.budget_amount)} budget</span>}
              </div>
              <button onClick={() => deleteProject(p.id).then(load).catch(() => toast.error('Error'))} className="text-xs text-rose-500 hover:underline mt-2">
                <Trash2 className="w-3 h-3 inline" /> Eliminar
              </button>
            </div>
          ))}
        </div>
      )}

      {tab === 'time' && (
        <div className="space-y-3">
          {selectedEntries.length > 0 && (
            <div className="bg-stone-900 text-white rounded-xl p-3 flex items-center gap-3 flex-wrap">
              <span className="font-semibold">{selectedEntries.length} registros seleccionados</span>
              <Select value={grouping} onValueChange={setGrouping}>
                <SelectTrigger className="w-40 bg-white text-stone-900"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="project">Por proyecto</SelectItem>
                  <SelectItem value="day">Por día</SelectItem>
                  <SelectItem value="single">Una línea</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleInvoice} className="bg-[#FF6B4A] text-white ml-auto" data-testid="invoice-entries-btn">
                <Receipt className="w-4 h-4 mr-1" /> Crear factura
              </Button>
              <Button variant="ghost" onClick={() => setSelectedEntries([])} className="text-white">Cancelar</Button>
            </div>
          )}
          <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
            {entries.length === 0 ? (
              <p className="p-10 text-center text-stone-500">Sin registros. Usa el cronómetro flotante o el botón "Registrar horas".</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-xs uppercase text-stone-500">
                  <tr>
                    <th className="px-2 py-2 w-8"></th>
                    <th className="text-left px-3 py-2">Fecha</th>
                    <th className="text-left px-3 py-2">Descripción</th>
                    <th className="text-left px-3 py-2">Proyecto</th>
                    <th className="text-right px-3 py-2">Duración</th>
                    <th className="text-right px-3 py-2">Tarifa</th>
                    <th className="text-right px-3 py-2">Importe</th>
                    <th className="text-center px-3 py-2">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => {
                    const proj = projects.find((p) => p.id === e.project_id);
                    const amount = (e.duration_minutes / 60) * (e.hourly_rate || 0);
                    return (
                      <tr key={e.id} className="border-t border-stone-100 hover:bg-stone-50" data-testid={`entry-row-${e.id}`}>
                        <td className="px-2 py-2 text-center">
                          {!e.invoiced && e.billable && (
                            <input type="checkbox" checked={selectedEntries.includes(e.id)} onChange={() => toggleSelect(e.id)} data-testid={`entry-check-${e.id}`} />
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{e.date}</td>
                        <td className="px-3 py-2">{e.description || '—'}</td>
                        <td className="px-3 py-2">
                          {proj ? (
                            <span className="inline-flex items-center gap-1 text-xs"><span className="w-2 h-2 rounded-full" style={{ background: proj.color }} />{proj.name}</span>
                          ) : <span className="text-stone-400">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">{fmtHours(e.duration_minutes)}</td>
                        <td className="px-3 py-2 text-right">{fmt(e.hourly_rate || 0)}/h</td>
                        <td className="px-3 py-2 text-right font-bold">{fmt(amount)}</td>
                        <td className="px-3 py-2 text-center">
                          {e.invoiced ? <Badge className="bg-emerald-100 text-emerald-700 border-0">Facturado</Badge> :
                           e.billable ? <Badge className="bg-amber-100 text-amber-700 border-0">Pendiente</Badge> :
                           <Badge className="bg-stone-100 text-stone-500 border-0">No facturable</Badge>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {tab === 'profit' && prof && (
        <div className="space-y-4" data-testid="profitability-tab">
          {!prof.has_internal_cost && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900 flex gap-2">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              <div>
                <p className="font-semibold">Coste interno no definido</p>
                <p className="text-xs">Configura tu coste por hora en Ajustes → empresa para ver márgenes reales. Sin él, el coste de tiempo se calcula como 0€.</p>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Metric icon={DollarSign} label="Ingresos" value={fmt(prof.totals.revenue)} color="#10B981" />
            <Metric icon={Hash} label="Gastos" value={fmt(prof.totals.expenses)} color="#F43F5E" />
            <Metric icon={Clock} label="Coste tiempo" value={fmt(prof.totals.time_cost)} color="#F59E0B" />
            <Metric icon={TrendingUp} label="Margen" value={fmt(prof.totals.margin)} color={prof.totals.margin >= 0 ? '#10B981' : '#F43F5E'} highlight />
            <Metric icon={Sparkles} label="Horas totales" value={`${prof.totals.hours}h`} color="#8B5CF6" />
          </div>

          {prof.losing_projects.length > 0 && (
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4">
              <p className="font-bold text-rose-900 mb-2 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Proyectos en pérdida ({prof.losing_projects.length})</p>
              <div className="space-y-1">
                {prof.losing_projects.map((p) => (
                  <div key={p.project_id} className="flex justify-between text-sm">
                    <span>{p.project_name} <span className="text-stone-500 text-xs">— {p.client_name}</span></span>
                    <span className="font-mono font-bold text-rose-700">{fmt(p.margin)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <ProjectsTable title="Top por margen absoluto" rows={prof.top_by_margin_abs} sortKey="margin" />
          <ProjectsTable title="Top por % margen" rows={prof.top_by_margin_pct} sortKey="margin_pct" />

          <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
            <div className="p-4 border-b border-stone-100"><h2 className="font-bold text-stone-800">Top clientes</h2></div>
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-xs uppercase text-stone-500">
                <tr><th className="text-left px-3 py-2">Cliente</th><th className="text-right px-3 py-2">Ingresos</th><th className="text-right px-3 py-2">Horas</th><th className="text-right px-3 py-2">Días cobro</th><th className="text-right px-3 py-2">Margen</th></tr>
              </thead>
              <tbody>
                {profClients.slice(0, 10).map((c) => (
                  <tr key={c.client_id} className="border-t border-stone-100">
                    <td className="px-3 py-2 font-medium">{c.client_name}</td>
                    <td className="px-3 py-2 text-right">{fmt(c.revenue_total)}</td>
                    <td className="px-3 py-2 text-right">{c.hours_total}h</td>
                    <td className="px-3 py-2 text-right">{c.avg_payment_days !== null ? `${c.avg_payment_days}d` : '—'}</td>
                    <td className={`px-3 py-2 text-right font-bold ${c.margin >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{fmt(c.margin)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-2xl border border-stone-100 p-4">
              <p className="text-xs uppercase font-semibold text-stone-500">Distribución de horas</p>
              <div className="mt-3 space-y-2">
                <div className="flex justify-between text-sm"><span>Facturables</span><span className="font-bold">{prof.time_distribution.billable_hours}h</span></div>
                <div className="flex justify-between text-sm"><span>No facturables</span><span className="font-bold text-stone-500">{prof.time_distribution.non_billable_hours}h</span></div>
                <div className="h-2 bg-stone-100 rounded-full overflow-hidden flex">
                  <div className="bg-emerald-400" style={{ width: `${prof.time_distribution.billable_hours / Math.max(0.01, prof.time_distribution.billable_hours + prof.time_distribution.non_billable_hours) * 100}%` }} />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-stone-100 p-4">
              <p className="text-xs uppercase font-semibold text-stone-500">Servicios más rentables</p>
              <div className="mt-2 space-y-1 text-sm max-h-32 overflow-y-auto">
                {profServices.slice(0, 5).map((s) => (
                  <div key={s.service_id} className="flex justify-between"><span className="truncate">{s.service_name}</span><span className="font-bold">{fmt(s.margin)}</span></div>
                ))}
                {profServices.length === 0 && <span className="text-stone-400 text-xs">Sin datos aún</span>}
              </div>
            </div>
          </div>
        </div>
      )}

      <CreateProjectDialog
        open={showProject}
        onClose={() => setShowProject(false)}
        clients={clients}
        onCreated={() => { setShowProject(false); load(); }}
      />
      <CreateEntryDialog
        open={showEntry}
        onClose={() => setShowEntry(false)}
        projects={projects}
        onCreated={() => { setShowEntry(false); load(); }}
      />
    </div>
  );
};

const Metric = ({ icon: Icon, label, value, color, highlight }) => (
  <div className={`bg-white rounded-2xl border p-4 ${highlight ? 'shadow-md' : ''}`} style={{ borderColor: highlight ? color : '#f5f5f4' }}>
    <div className="flex items-center justify-between mb-1">
      <span className="text-[10px] uppercase font-semibold text-stone-500">{label}</span>
      <Icon className="w-4 h-4" style={{ color }} />
    </div>
    <p className="text-xl font-bold text-stone-900">{value}</p>
  </div>
);

const ProjectsTable = ({ title, rows, sortKey }) => (
  <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
    <div className="p-4 border-b border-stone-100"><h2 className="font-bold text-stone-800">{title}</h2></div>
    {rows.length === 0 ? (
      <p className="text-center py-6 text-stone-400 text-sm">Sin datos</p>
    ) : (
      <table className="w-full text-sm">
        <thead className="bg-stone-50 text-xs uppercase text-stone-500">
          <tr>
            <th className="text-left px-3 py-2">Proyecto</th>
            <th className="text-right px-3 py-2">Ingresos</th>
            <th className="text-right px-3 py-2">Horas</th>
            <th className="text-right px-3 py-2">Tarifa efect.</th>
            <th className="text-right px-3 py-2">Margen</th>
            <th className="text-right px-3 py-2">%</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.project_id} className="border-t border-stone-100">
              <td className="px-3 py-2">
                <p className="font-medium">{p.project_name}</p>
                <p className="text-[10px] text-stone-400">{p.client_name}</p>
              </td>
              <td className="px-3 py-2 text-right">{fmt(p.revenue_total)}</td>
              <td className="px-3 py-2 text-right">{p.hours_total}h</td>
              <td className="px-3 py-2 text-right">{fmt(p.effective_hourly_rate)}/h</td>
              <td className={`px-3 py-2 text-right font-bold ${p.margin >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{fmt(p.margin)}</td>
              <td className={`px-3 py-2 text-right font-mono ${p.margin_pct >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{p.margin_pct}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

const CreateProjectDialog = ({ open, onClose, clients, onCreated }) => {
  const [form, setForm] = useState({ client_id: '', name: '', billing_type: 'hourly', default_hourly_rate: '', budget_amount: '', color: '#FF6B4A' });
  const submit = async () => {
    if (!form.client_id || !form.name) return toast.error('Cliente y nombre obligatorios');
    try {
      const payload = { ...form };
      if (!payload.default_hourly_rate) delete payload.default_hourly_rate;
      else payload.default_hourly_rate = parseFloat(payload.default_hourly_rate);
      if (!payload.budget_amount) delete payload.budget_amount;
      else payload.budget_amount = parseFloat(payload.budget_amount);
      await createProject(payload);
      toast.success('Proyecto creado');
      setForm({ client_id: '', name: '', billing_type: 'hourly', default_hourly_rate: '', budget_amount: '', color: '#FF6B4A' });
      onCreated();
    } catch (e) {
      toast.error('Error');
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-project-dialog">
        <DialogHeader><DialogTitle>Nuevo proyecto</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Cliente *</Label>
            <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
              <SelectTrigger data-testid="proj-client"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
              <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Nombre *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Web rediseño 2026" data-testid="proj-name" /></div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label>Tipo</Label>
              <Select value={form.billing_type} onValueChange={(v) => setForm({ ...form, billing_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Por hora</SelectItem>
                  <SelectItem value="fixed_price">Precio fijo</SelectItem>
                  <SelectItem value="mixed">Mixto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Tarifa €/h</Label><Input type="number" value={form.default_hourly_rate} onChange={(e) => setForm({ ...form, default_hourly_rate: e.target.value })} placeholder="65" /></div>
            <div><Label>Presupuesto €</Label><Input type="number" value={form.budget_amount} onChange={(e) => setForm({ ...form, budget_amount: e.target.value })} placeholder="5000" /></div>
          </div>
          <div><Label>Color</Label><Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-20 h-10 p-1" /></div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={submit} className="bg-[#FF6B4A] text-white" data-testid="save-project-btn">Crear</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CreateEntryDialog = ({ open, onClose, projects, onCreated }) => {
  const [form, setForm] = useState({
    project_id: '', description: '', date: new Date().toISOString().slice(0, 10),
    hours: '', minutes: '0', billable: true,
  });
  const submit = async () => {
    const totalMin = (parseInt(form.hours || 0) * 60) + parseInt(form.minutes || 0);
    if (!totalMin) return toast.error('Indica duración');
    try {
      const payload = {
        project_id: form.project_id || null,
        description: form.description,
        date: form.date,
        duration_minutes: totalMin,
        billable: form.billable,
      };
      if (!payload.project_id) delete payload.project_id;
      await createTimeEntry(payload);
      toast.success('Registro creado');
      setForm({ project_id: '', description: '', date: new Date().toISOString().slice(0, 10), hours: '', minutes: '0', billable: true });
      onCreated();
    } catch (e) { toast.error('Error'); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-entry-dialog">
        <DialogHeader><DialogTitle>Registrar horas</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Proyecto</Label>
            <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
              <SelectTrigger data-testid="entry-project"><SelectValue placeholder="Sin proyecto" /></SelectTrigger>
              <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Descripción</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Diseño landing page" data-testid="entry-desc" /></div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label>Fecha</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div><Label>Horas</Label><Input type="number" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} placeholder="2" data-testid="entry-hours" /></div>
            <div><Label>Min</Label><Input type="number" value={form.minutes} onChange={(e) => setForm({ ...form, minutes: e.target.value })} placeholder="30" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.billable} onChange={(e) => setForm({ ...form, billable: e.target.checked })} /> Facturable
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={submit} className="bg-[#FF6B4A] text-white" data-testid="save-entry-btn">Guardar</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectsPage;
